import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { TIME_SLOTS, getEndSlots, calcDurationHours } from '../utils/timeSlots';
import { usePromotion } from '../lib/promotions';
import {
  useOfferCustomer, offerPreviewFor, OfferPreviewLine, WalkInOfferPanel, OfferRewardsNotice,
  SellCreditsModal, walkInBookingOfferLive, offerResultNotes,
} from '../components/WalkInSaleOffer';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Loader2, CalendarDays, IndianRupee, CheckCircle2, Clock, UserPlus, UserCog, Send, LogOut, Sun, Moon, TrendingUp, Link2, AlertCircle, Eye, Copy, ExternalLink, Download, Share2, Building2, Briefcase, FileText, CalendarClock, Plus, Trash2, Ticket, Contact, ChevronLeft, ChevronRight, Coins } from 'lucide-react';
import { shareViaWhatsApp, WhatsAppIcon } from '../lib/whatsapp';
import { ApprovalsModule } from '../components/approvals/ApprovalsModule';
import VirtualOfficeShareTab from '../components/virtual_office/VirtualOfficeShareTab';
import VirtualOfficeSubscriptionsTab from '../components/virtual_office/VirtualOfficeSubscriptionsTab';
import { ApprovalsBell } from '../components/approvals/ApprovalsBell';
import { toast } from 'sonner';
import AvailabilityView from '../components/AvailabilityView';
import { ProfileModal } from '../components/ProfileModal';
import RoleAnalytics from '../components/analytics/RoleAnalytics';
import { InventoryTab } from '../components/inventory/InventoryTab';
import { ClientDatabaseTab } from '../components/clients/ClientDatabaseTab';
import { MiscPaymentLinkModal } from '../components/sales/MiscPaymentLinkModal';
import { CreateClientModal } from '../components/sales/CreateClientModal';
import { SalesDayPassesTab } from '../components/day_passes/SalesDayPassesTab';
import { RescheduleBookingModal } from '../components/bookings/RescheduleBookingModal';
import { useHolidays } from '../hooks/useQueries';
import { Link } from 'react-router-dom';
import { Presentation } from 'lucide-react';
import { useStudioAccess } from '../components/proposal_studio/useStudioAccess';

const SalesDashboard = () => {
  const { user, api, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { data: holidaysList = [] } = useHolidays();
  const studioAccess = useStudioAccess(api);
  const holidayDates = useMemo(() => {
    const map = {};
    holidaysList.forEach((h) => { map[h.date] = h.name; });
    return map;
  }, [holidaysList]);

  const [stats, setStats] = useState(null);
  const [venues, setVenues] = useState([]);
  const [bookables, setBookables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGuestBooking, setShowGuestBooking] = useState(false);
  const [showSellCredits, setShowSellCredits] = useState(false);
  const [showMiscPay, setShowMiscPay] = useState(false);
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeView, setActiveView] = useState('dashboard');
  // Recent Bookings page; a ref so refreshes after an action stay on it.
  const bookingsPageRef = useRef(1);
  const [bookingsPageLoading, setBookingsPageLoading] = useState(false);

  const emptySlot = () => ({
    _key: Math.random().toString(36).slice(2),
    venue_id: '', bookable_id: '', booking_date: '', start_time: '', end_time: '',
    discount_percent: 0, parking_qty: 0, parking_2w_qty: 0, pass_quantity: 1, pass_days: 1,
    pricePreview: null, loadingPreview: false,
  });
  const [guestForm, setGuestForm] = useState({
    guest_name: '', guest_email: '', guest_phone: '',
    gstin: '', company_name: '', company_address: '',
    is_partial_payment: false, partial_amount: '', balance_due_date: '',
    payment_method: 'razorpay',
    apply_sale_offer: false,
  });
  const [slots, setSlots] = useState([emptySlot()]);
  const [paymentLinkResult, setPaymentLinkResult] = useState(null);

  // Sale offer applied for this guest (Diwali etc.) — same rules as the CM walk-in form.
  const promo = usePromotion();
  const applyOffer = walkInBookingOfferLive() && !!guestForm.apply_sale_offer;
  const offerAccount = useOfferCustomer(applyOffer ? guestForm.guest_email : '');
  const toggleSaleOffer = (on) => {
    setGuestForm(p => ({ ...p, apply_sale_offer: on, is_partial_payment: on ? false : p.is_partial_payment }));
    // Manual discounts are off with the offer; re-preview any slot that had one.
    if (on) setSlots(prev => prev.map(s => (s.discount_percent ? { ...s, discount_percent: 0, pricePreview: null } : s)));
  };

  // Any field edit invalidates that slot's price preview.
  const updateSlot = (index, patch) => {
    setSlots(prev => prev.map((s, i) => (i === index ? { ...s, ...patch, pricePreview: null } : s)));
  };
  const setSlotPreview = (index, pricePreview) => {
    setSlots(prev => prev.map((s, i) => (i === index ? { ...s, pricePreview } : s)));
  };
  const setSlotLoading = (index, loadingPreview) => {
    setSlots(prev => prev.map((s, i) => (i === index ? { ...s, loadingPreview } : s)));
  };
  const addSlot = () => setSlots(prev => [...prev, emptySlot()]);
  const removeSlot = (index) => setSlots(prev => prev.filter((_, i) => i !== index));

  const allSlotsPreviewed = slots.length > 0 && slots.every(s => s.pricePreview);
  const grandTotal = slots.reduce((sum, s) => sum + (s.pricePreview?.total_payable || 0), 0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, venuesRes, bookablesRes] = await Promise.all([
        api.get('/bookings/sales/stats', { params: { page: bookingsPageRef.current } }),
        api.get('/venues').catch(() => ({ data: [] })),
        api.get('/bookables').catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setVenues(Array.isArray(venuesRes.data) ? venuesRes.data : []);
      setBookables(Array.isArray(bookablesRes.data) ? bookablesRes.data : []);
    } catch (err) {
      console.error('Failed to load sales data:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const goToBookingsPage = async (page) => {
    setBookingsPageLoading(true);
    try {
      const res = await api.get('/bookings/sales/stats', { params: { page } });
      bookingsPageRef.current = res.data.recent_page || page;
      setStats(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load bookings');
    } finally {
      setBookingsPageLoading(false);
    }
  };

  const filteredBookablesFor = (venueId) => venueId
    ? bookables.filter(b => b.venue_id === venueId && b.is_active !== false)
    : [];

  const handleGuestBooking = async () => {
    for (const s of slots) {
      if (s.booking_date && holidayDates[s.booking_date]) {
        toast.error(`Closed on ${s.booking_date} (${holidayDates[s.booking_date]}). Please choose another date.`);
        return;
      }
    }
    // Validate GSTIN flow: if GSTIN is filled, company name & address are required
    const gstinTrim = (guestForm.gstin || '').trim();
    if (gstinTrim) {
      if (gstinTrim.length !== 15) {
        toast.error('GSTIN must be 15 characters');
        return;
      }
      if (!guestForm.company_name?.trim() || !guestForm.company_address?.trim()) {
        toast.error('Company name and address are required when GSTIN is provided');
        return;
      }
    }
    const isCash = guestForm.payment_method === 'manual';
    if (!isCash && !guestForm.guest_email) { toast.error('Guest email is required to send a payment link. Choose Manual / Cash if the client has no email.'); return; }
    if (applyOffer && !guestForm.guest_email) { toast.error("Guest email is required to apply the sale offer. The rewards go to their Clayworks account."); return; }
    if (applyOffer && offerAccount.data && !offerAccount.data.eligible) { toast.error(offerAccount.data.reason); return; }
    const isPartial = !isCash && !applyOffer && !!guestForm.is_partial_payment;
    if (isPartial) {
      const partial = parseFloat(guestForm.partial_amount) || 0;
      const total = grandTotal || 0;
      if (partial <= 0) { toast.error('Partial payment amount must be greater than 0'); return; }
      if (total && partial >= total) { toast.error(`Partial amount must be less than the total (₹${total.toLocaleString('en-IN')})`); return; }
      if (!guestForm.balance_due_date) { toast.error('Balance due date is required for partial payment'); return; }
    }
    setSubmitting(true);
    try {
      const payload = {
        guest_name: guestForm.guest_name,
        guest_email: guestForm.guest_email,
        guest_phone: guestForm.guest_phone,
        gstin: guestForm.gstin,
        company_name: guestForm.company_name,
        company_address: guestForm.company_address,
        payment_method: isCash ? 'cash' : 'razorpay',
        apply_sale_offer: applyOffer,
        is_partial_payment: isPartial,
        partial_amount: isPartial ? parseFloat(guestForm.partial_amount) : undefined,
        balance_due_date: isPartial ? guestForm.balance_due_date : undefined,
        slots: slots.map(s => ({
          venue_id: s.venue_id,
          bookable_id: s.bookable_id,
          booking_date: s.booking_date,
          start_time: s.start_time,
          end_time: s.end_time,
          discount_percent: s.discount_percent || 0,
          parking_qty: s.parking_qty || 0,
          parking_2w_qty: s.parking_2w_qty || 0,
          pass_quantity: s.pass_quantity || 1,
          pass_days: s.pass_days || 1,
        })),
      };
      const res = await api.post('/bookings/guest-multi', payload);
      const notes = offerResultNotes(res.data.sale_offer);
      if (isCash) toast.success(`${res.data.message || 'Booking confirmed (paid via cash)'}${notes.earned}${notes.account}`, { duration: 8000 });
      setShowGuestBooking(false);
      setGuestForm({
        guest_name: '', guest_email: '', guest_phone: '',
        gstin: '', company_name: '', company_address: '',
        is_partial_payment: false, partial_amount: '', balance_due_date: '',
        payment_method: 'razorpay',
        apply_sale_offer: false,
      });
      setSlots([emptySlot()]);
      if (!isCash) setPaymentLinkResult({
        payment_link: res.data.payment_link,
        balance_payment_link: res.data.balance_payment_link,
        is_partial_payment: res.data.is_partial_payment,
        partial_amount: res.data.partial_amount,
        balance_amount: res.data.balance_amount,
        balance_due_date: res.data.balance_due_date,
        total_amount: res.data.total_amount,
        slots: res.data.slots,
        guest_email: guestForm.guest_email,
        guest_name: guestForm.guest_name,
        guest_phone: guestForm.guest_phone,
        sale_offer: res.data.sale_offer,
        message: (res.data.message || '') + notes.account,
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create guest booking');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePricePreview = async (index) => {
    const slot = slots[index];
    const { bookable_id, start_time, end_time } = slot;
    if (!bookable_id || !start_time || !end_time) {
      toast.error('Select space, start and end time to preview cost');
      return;
    }
    setSlotLoading(index, true);
    try {
      const res = await api.post('/bookings/price-preview', {
        bookable_id,
        start_time,
        end_time,
        discount_percent: slot.discount_percent || 0,
        pass_quantity: Math.max(1, parseInt(slot.pass_quantity) || 1),
        pass_days: Math.max(1, parseInt(slot.pass_days) || 1),
      });
      setSlotPreview(index, res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to calculate price');
    } finally {
      setSlotLoading(index, false);
    }
  };

  const copyPaymentLink = () => {
    if (paymentLinkResult?.payment_link) {
      navigator.clipboard.writeText(paymentLinkResult.payment_link);
      toast.success('Payment link copied to clipboard');
    }
  };

  const handleDownloadInvoice = async (bookingId) => {
    try {
      toast.info('Downloading invoice...');
      // Ensure invoice exists first
      await api.get(`/bookings/${bookingId}/invoice`);
      const res = await api.get(`/bookings/${bookingId}/invoice/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Clayworks_Invoice_${bookingId.slice(0, 8)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download invoice');
    }
  };

  const cardBg = isDark ? 'bg-[#13161a] border-[#1e2128]' : 'bg-white border-[#e5dfd0]';
  const cardBorder = isDark ? 'border-[#1e2128]' : 'border-[#e5dfd0]';
  const inputBg = isDark ? 'bg-[#0a0d10]' : 'bg-white';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const statCards = [
    { label: 'Total Bookings', value: stats?.total_bookings || 0, icon: CalendarDays, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { label: 'Confirmed', value: stats?.confirmed_bookings || 0, icon: CheckCircle2, color: 'text-green-500', bg: 'bg-green-500/10' },
    { label: 'Pending Payment', value: stats?.pending_bookings || 0, icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
    { label: 'Total Revenue', value: `${(stats?.total_revenue || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}`, icon: IndianRupee, color: 'text-primary', bg: 'bg-primary/10' },
    { label: 'This Month', value: `${(stats?.monthly_revenue || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}`, icon: TrendingUp, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  ];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0a0b0d]' : 'bg-[#f7f3e8]'}`}>
      {/* Header */}
      <div className={`border-b ${isDark ? 'bg-[#0a0b0d] border-[#1e2128]' : 'bg-[#f7f3e8] border-[#e5dfd0]'}`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/logo.webp" alt="Clayworks" className="h-8 sm:h-9 w-auto" />
            <div>
              <h1 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Sales Dashboard</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">Welcome, {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <Button
              onClick={() => setShowGuestBooking(true)}
              className="bg-primary hover:bg-primary/90"
              size="sm"
              data-testid="sales-guest-booking-btn"
            >
              <UserPlus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Guest Booking</span>
            </Button>
            <Button
              onClick={() => setShowSellCredits(true)}
              variant="outline"
              size="sm"
              data-testid="sales-sell-credits-btn"
            >
              <Coins className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Sell Credits</span>
            </Button>
            <Button
              onClick={() => setShowCreateClient(true)}
              variant="outline"
              size="sm"
              data-testid="sales-create-client-btn"
            >
              <UserCog className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Create Client</span>
            </Button>
            <Button
              onClick={() => setShowMiscPay(true)}
              variant="outline"
              size="sm"
              data-testid="sales-misc-pay-btn"
              title="Generate a Razorpay link for any custom amount — auto-invoiced via Zoho"
            >
              <Link2 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Custom Payment Link</span>
            </Button>
            <ApprovalsBell api={api} user={user} onClick={() => setActiveView('approvals')} />
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-muted/50 text-muted-foreground" aria-label="Toggle theme">
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button onClick={() => setShowProfileModal(true)} className="p-2 rounded-lg hover:bg-muted/50 text-muted-foreground" aria-label="Profile" data-testid="sidebar-profile-btn">
              {user?.photo_url ? (
                <img src={user.photo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${user.photo_url}` : user.photo_url} alt="" className="w-6 h-6 rounded-full object-cover" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-medium text-primary">{user?.name?.charAt(0)}</div>
              )}
            </button>
            <button onClick={logout} className="p-2 rounded-lg hover:bg-muted/50 text-muted-foreground" aria-label="Logout">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={`border-b ${isDark ? 'border-[#1e2128]' : 'border-[#e5dfd0]'}`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 flex items-center gap-1 overflow-x-auto clay-scroll-x">
          <button
            onClick={() => setActiveView('dashboard')}
            className={`shrink-0 whitespace-nowrap px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeView === 'dashboard'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="sales-tab-dashboard"
          >
            <TrendingUp className="w-4 h-4 inline mr-1.5" />
            Dashboard
          </button>
          <button
            onClick={() => setActiveView('availability')}
            className={`shrink-0 whitespace-nowrap px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeView === 'availability'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="sales-tab-availability"
          >
            <Eye className="w-4 h-4 inline mr-1.5" />
            Availability
          </button>
          <button
            onClick={() => setActiveView('spaces')}
            className={`shrink-0 whitespace-nowrap px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeView === 'spaces'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="sales-tab-spaces"
          >
            <Share2 className="w-4 h-4 inline mr-1.5" />
            Share Spaces
          </button>
          <button
            onClick={() => setActiveView('approvals')}
            className={`shrink-0 whitespace-nowrap px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeView === 'approvals'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="sales-tab-approvals"
          >
            <Send className="w-4 h-4 inline mr-1.5" />
            Approvals
          </button>
          <button
            onClick={() => setActiveView('virtual_office')}
            className={`shrink-0 whitespace-nowrap px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeView === 'virtual_office'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="sales-tab-virtual-office"
          >
            <Briefcase className="w-4 h-4 inline mr-1.5" />
            VO Share
          </button>
          <button
            onClick={() => setActiveView('vo_subscriptions')}
            className={`shrink-0 whitespace-nowrap px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeView === 'vo_subscriptions'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="sales-tab-vo-subs"
          >
            <FileText className="w-4 h-4 inline mr-1.5" />
            VO Subscriptions
          </button>
          <button
            onClick={() => setActiveView('day_passes')}
            className={`shrink-0 whitespace-nowrap px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeView === 'day_passes'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="sales-tab-day-passes"
          >
            <Ticket className="w-4 h-4 inline mr-1.5" />
            Bulk Day Passes
          </button>
          <button
            onClick={() => setActiveView('inventory')}
            className={`shrink-0 whitespace-nowrap px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeView === 'inventory'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="sales-tab-inventory"
          >
            <Building2 className="w-4 h-4 inline mr-1.5" />
            Inventory
          </button>
          <button
            onClick={() => setActiveView('client_database')}
            className={`shrink-0 whitespace-nowrap px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeView === 'client_database'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="sales-tab-client-database"
          >
            <Contact className="w-4 h-4 inline mr-1.5" />
            Client Database
          </button>
          {studioAccess && (
            <Link
              to="/proposal-studio"
              className="shrink-0 whitespace-nowrap px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors border-transparent text-muted-foreground hover:text-foreground"
              data-testid="sales-tab-proposal-studio"
            >
              <Presentation className="w-4 h-4 inline mr-1.5" />
              Proposal Studio
            </Link>
          )}
          <button
            onClick={() => setActiveView('analytics')}
            className={`shrink-0 whitespace-nowrap px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeView === 'analytics'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="sales-tab-analytics"
          >
            <TrendingUp className="w-4 h-4 inline mr-1.5" />
            Analytics
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-8 space-y-6 sm:space-y-8">
        {activeView === 'availability' ? (
          <AvailabilityView venues={venues} />
        ) : activeView === 'spaces' ? (
          <SpacesShareView venues={venues} bookables={bookables} userId={user?.id} isDark={isDark} />
        ) : activeView === 'approvals' ? (
          <ApprovalsModule api={api} user={user} />
        ) : activeView === 'virtual_office' ? (
          <VirtualOfficeShareTab api={api} user={user} theme={{ cardBg }} />
        ) : activeView === 'vo_subscriptions' ? (
          <VirtualOfficeSubscriptionsTab api={api} theme={{ cardBg, cardBorder, inputBg }} isAdmin={false} />
        ) : activeView === 'analytics' ? (
          <RoleAnalytics role="sales" />
        ) : activeView === 'inventory' ? (
          <InventoryTab api={api} user={user} venues={venues} />
        ) : activeView === 'client_database' ? (
          <ClientDatabaseTab user={user} />
        ) : activeView === 'day_passes' ? (
          <SalesDayPassesTab api={api} user={user} venues={venues} />
        ) : (
        <>
        {/* Stat Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {statCards.map((s, i) => {
            const Icon = s.icon;
            return (
              <Card key={i} className={cardBg}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center shrink-0`}>
                      <Icon className={`w-5 h-5 ${s.color}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">{s.label}</p>
                      <p className="text-lg font-bold break-words" data-testid={`sales-stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>{s.value}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* My Guest Booking Revenue */}
        <div>
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            My Guest Booking Revenue
          </h2>
          <Card className={cardBg}>
            <CardContent className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 text-center">
                <div className="min-w-0">
                  <p className="text-2xl sm:text-3xl font-bold text-primary break-words">{stats?.monthly_bookings || 0}</p>
                  <p className="text-sm text-muted-foreground mt-1">This Month Bookings</p>
                </div>
                <div className="min-w-0">
                  <p className="text-2xl sm:text-3xl font-bold text-emerald-500 break-words">
                    {(stats?.monthly_revenue || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">This Month Revenue</p>
                </div>
                <div className="min-w-0">
                  <p className="text-2xl sm:text-3xl font-bold break-words">
                    {(stats?.total_revenue || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">All-Time Revenue</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Bookings */}
        <div>
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-primary" />
            Recent Bookings
          </h2>
          <div className="space-y-3">
            {(!stats?.recent_bookings || stats.recent_bookings.length === 0) ? (
              <Card className={cardBg}>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <CalendarDays className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>No bookings yet. Create your first guest booking!</p>
                </CardContent>
              </Card>
            ) : (
              stats.recent_bookings.map(b => (
                <Card key={b.id} className={cardBg} data-testid={b.is_group ? `sales-booking-group-${b.id.slice(0, 8)}` : undefined}>
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                          b.status === 'confirmed' || b.status === 'completed' ? 'bg-green-500/10' :
                          b.status === 'pending_payment' ? 'bg-yellow-500/10' :
                          b.status === 'cancelled' ? 'bg-red-500/10' : 'bg-muted'
                        }`}>
                          {b.status === 'confirmed' || b.status === 'completed' ? <CheckCircle2 className="w-5 h-5 text-green-500" /> :
                           b.status === 'pending_payment' ? <Clock className="w-5 h-5 text-yellow-500" /> :
                           b.status === 'cancelled' ? <AlertCircle className="w-5 h-5 text-red-500" /> :
                           <CalendarDays className="w-5 h-5 text-muted-foreground" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm flex items-center gap-1.5 min-w-0">
                            <span className="truncate">
                              {b.notes?.startsWith('Walk-in:')
                                ? b.notes.split('\n')[0].replace('Walk-in: ', '').split(' | ')[0]
                                : b.guest_name || b.guest_email || b.bookable_name || 'Booking'}
                            </span>
                            {b.is_group && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-primary/10 text-primary shrink-0">
                                {b.slot_count} slots
                              </span>
                            )}
                          </p>
                          {b.is_group ? (
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">{b.bookable_name}</p>
                          ) : (
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground mt-0.5">
                              <span className="truncate max-w-[10rem]">{b.bookable_name || ''}</span>
                              <span>{b.booking_date}</span>
                              <span>{b.start_time} - {b.end_time}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center flex-wrap gap-2.5 sm:gap-3 sm:justify-end sm:shrink-0">
                        <span className="text-sm font-semibold shrink-0">
                          {b.total_amount ? `₹${b.total_amount.toLocaleString()}` : ''}
                        </span>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium shrink-0 ${
                          b.status === 'confirmed' || b.status === 'completed' ? 'bg-green-500/10 text-green-500' :
                          b.status === 'pending_payment' ? 'bg-yellow-500/10 text-yellow-500' :
                          b.status === 'cancelled' ? 'bg-red-500/10 text-red-500' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {b.status === 'pending_payment' ? 'Awaiting Payment' : b.status}
                        </span>
                        {b.razorpay_payment_link_url && b.status === 'pending_payment' && (
                          <>
                            <a href={b.razorpay_payment_link_url} target="_blank" rel="noopener noreferrer"
                              className="text-primary hover:text-primary/80 shrink-0" title="Open payment link">
                              <Link2 className="w-4 h-4" />
                            </a>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(b.razorpay_payment_link_url);
                                toast.success('Payment link copied');
                              }}
                              className="text-muted-foreground hover:text-primary transition-colors shrink-0"
                              title="Copy payment link"
                              data-testid={`sales-copy-paylink-${b.id.slice(0, 8)}`}
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => shareViaWhatsApp(
                                `Hi ${b.guest_name || ''}, here's your Clayworks payment link for ₹${b.total_amount?.toLocaleString('en-IN')}: ${b.razorpay_payment_link_url}`,
                                b.guest_phone
                              )}
                              className="text-green-500 hover:text-green-600 transition-colors shrink-0"
                              title="Share via WhatsApp"
                              data-testid={`sales-wa-paylink-${b.id.slice(0, 8)}`}
                            >
                              <WhatsAppIcon className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {!b.is_group && ['pending', 'pending_payment', 'confirmed'].includes(b.status) && (
                          <button
                            onClick={() => setRescheduleBooking(b)}
                            className="text-blue-500 hover:text-blue-600 shrink-0"
                            title="Reschedule (no charge, client is notified)"
                            data-testid={`sales-reschedule-${b.id.slice(0, 8)}`}
                          >
                            <CalendarClock className="w-4 h-4" />
                          </button>
                        )}
                        {b.status === 'pending_payment' && (
                          <button
                            onClick={async () => {
                              if (!window.confirm(b.is_group ? `Confirm all ${b.slot_count} slots in this booking have been paid?` : 'Confirm this booking has been paid?')) return;
                              try {
                                await api.post(`/bookings/${b.id}/confirm-payment`, { payment_reference: 'manual' });
                                toast.success('Booking confirmed as paid!');
                                fetchData();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to confirm');
                              }
                            }}
                            className="text-green-500 hover:text-green-600 transition-colors shrink-0"
                            title="Mark as Paid"
                            data-testid={`sales-confirm-${b.id.slice(0, 8)}`}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                        )}
                        {(b.total_amount || 0) > 0 && (b.credits_used || 0) === 0 && !b.is_internal && (
                          <button onClick={() => handleDownloadInvoice(b.id)}
                            className="text-muted-foreground hover:text-primary transition-colors shrink-0"
                            title={b.is_group ? 'Download consolidated invoice (all slots)' : 'Download Invoice'}
                            data-testid={`sales-invoice-${b.id.slice(0, 8)}`}>
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    {b.is_group && (
                      <div className="mt-3 pt-3 border-t border-border/50 space-y-1.5">
                        {b.slots.map(s => (
                          <div key={s.id} className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>{s.bookable_name} · {s.venue_name} · {s.booking_date}, {s.start_time}-{s.end_time}</span>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-foreground">₹{(s.total_amount || 0).toLocaleString('en-IN')}</span>
                              {['pending', 'pending_payment', 'confirmed'].includes(s.status) && (
                                <button
                                  onClick={() => setRescheduleBooking({ ...s, guest_name: b.guest_name, guest_email: b.guest_email, guest_phone: b.guest_phone })}
                                  className="text-blue-500 hover:text-blue-600"
                                  title="Reschedule this slot (no charge, client is notified)"
                                  data-testid={`sales-reschedule-${s.id.slice(0, 8)}`}
                                >
                                  <CalendarClock className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
          {stats?.recent_total_pages > 1 && (
            <div className="flex items-center justify-between mt-4" data-testid="sales-bookings-pagination">
              <p className="text-sm text-muted-foreground">
                Page {stats.recent_page} of {stats.recent_total_pages} · {stats.recent_total} bookings
              </p>
              <div className="flex items-center gap-2">
                {bookingsPageLoading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                <Button variant="outline" size="sm" disabled={bookingsPageLoading || stats.recent_page <= 1}
                  onClick={() => goToBookingsPage(stats.recent_page - 1)} data-testid="sales-bookings-prev">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                </Button>
                <Button variant="outline" size="sm" disabled={bookingsPageLoading || stats.recent_page >= stats.recent_total_pages}
                  onClick={() => goToBookingsPage(stats.recent_page + 1)} data-testid="sales-bookings-next">
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>
        </>
        )}
      </div>

      {/* Guest Booking Dialog */}
      <Dialog open={showGuestBooking} onOpenChange={(open) => { setShowGuestBooking(open); if (!open) setSlots([emptySlot()]); }}>
        <DialogContent className={`max-w-lg ${isDark ? 'bg-[#13161a] border-[#1e2128]' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-primary" />Create Guest Booking</DialogTitle>
            <DialogDescription>Book one or more workspace slots for a guest — add extra spaces, dates or times with "Add New Slot". One combined payment link covering everything is sent to their email.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[65vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Guest Name *</Label>
                <Input placeholder="Full name" value={guestForm.guest_name}
                  onChange={(e) => setGuestForm(p => ({ ...p, guest_name: e.target.value }))}
                  data-testid="guest-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Guest Phone</Label>
                <Input placeholder="+919876543210" value={guestForm.guest_phone}
                  onChange={(e) => setGuestForm(p => ({ ...p, guest_phone: e.target.value }))}
                  data-testid="guest-phone-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Guest Email *</Label>
              <Input type="email" placeholder="guest@email.com" value={guestForm.guest_email}
                onChange={(e) => setGuestForm(p => ({ ...p, guest_email: e.target.value }))}
                data-testid="guest-email-input" />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">GSTIN <span className="text-muted-foreground font-normal">(Optional · for B2B clients)</span></Label>
              <Input
                value={guestForm.gstin || ''}
                onChange={(e) => setGuestForm(p => ({ ...p, gstin: e.target.value.toUpperCase() }))}
                maxLength={15}
                placeholder="22AAAAA0000A1Z5"
                data-testid="guest-gstin-input"
              />
              <div className="grid grid-cols-1 gap-3 mt-2 p-3 rounded-md border border-primary/20 bg-primary/5">
                <div className="space-y-1">
                  <Label className="text-xs">Company Name {guestForm.gstin?.trim() ? '*' : <span className="text-muted-foreground font-normal">(Optional · appears on invoice)</span>}</Label>
                  <Input
                    value={guestForm.company_name || ''}
                    onChange={(e) => setGuestForm(p => ({ ...p, company_name: e.target.value }))}
                    placeholder="Registered company name"
                    data-testid="guest-company-name-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Company Address {guestForm.gstin?.trim() ? '*' : <span className="text-muted-foreground font-normal">(Optional · appears on invoice)</span>}</Label>
                  <Input
                    value={guestForm.company_address || ''}
                    onChange={(e) => setGuestForm(p => ({ ...p, company_address: e.target.value }))}
                    placeholder="Billing address"
                    data-testid="guest-company-address-input"
                  />
                </div>
              </div>
            </div>

            <WalkInOfferPanel
              promo={promo}
              checked={applyOffer}
              onCheckedChange={toggleSaleOffer}
              email={guestForm.guest_email}
              lookup={offerAccount}
              extraNote="Partial payment is off too: the offer needs full payment."
            />

            {slots.map((slot, index) => {
              const slotBookables = filteredBookablesFor(slot.venue_id);
              const selectedSpace = slotBookables.find(b => b.id === slot.bookable_id);
              const mode = selectedSpace?.booking_mode;
              return (
                <div key={slot._key} className={`rounded-xl border p-3 space-y-3 ${isDark ? 'border-[#1e2128]' : 'border-[#e5dfd0]'}`} data-testid={`guest-slot-${index}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">Slot {index + 1}</p>
                    {slots.length > 1 && (
                      <button type="button" onClick={() => removeSlot(index)} className="text-muted-foreground hover:text-red-500" title="Remove this slot" data-testid={`guest-slot-remove-${index}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Venue *</Label>
                    <Select value={slot.venue_id} onValueChange={(v) => updateSlot(index, { venue_id: v, bookable_id: '' })}>
                      <SelectTrigger data-testid={`guest-venue-select-${index}`}><SelectValue placeholder="Select venue" /></SelectTrigger>
                      <SelectContent>
                        {venues.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {slot.venue_id && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Space *</Label>
                      <Select value={slot.bookable_id} onValueChange={(v) => {
                        const selected = slotBookables.find(b => b.id === v);
                        const updates = { bookable_id: v };
                        if (selected?.booking_mode === 'daily') {
                          updates.start_time = '09:00';
                          updates.end_time = '20:00';
                        } else if (selected?.booking_mode === 'monthly') {
                          updates.start_time = '09:00';
                          updates.end_time = '18:00';
                        } else {
                          updates.start_time = '';
                          updates.end_time = '';
                        }
                        updateSlot(index, updates);
                      }}>
                        <SelectTrigger data-testid={`guest-bookable-select-${index}`}><SelectValue placeholder="Select space" /></SelectTrigger>
                        <SelectContent>
                          {slotBookables.map(b => (
                            <SelectItem key={b.id} value={b.id}>
                              {b.name} - ₹{b.hourly_rate || b.price_per_hour}{b.booking_mode === 'daily' ? '/day' : b.booking_mode === 'monthly' ? '/month' : '/hr'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Date *</Label>
                    <Input type="date" value={slot.booking_date}
                      onChange={(e) => updateSlot(index, { booking_date: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      data-testid={`guest-date-input-${index}`} />
                    {slot.booking_date && holidayDates[slot.booking_date] && (
                      <p className="text-xs text-red-500 mt-1">Closed — {holidayDates[slot.booking_date]}</p>
                    )}
                  </div>
                  {mode === 'daily' ? (
                    <div className="space-y-3">
                      <div className="p-3 rounded-lg bg-primary/10 border border-primary/20" data-testid={`guest-daypass-info-${index}`}>
                        <p className="text-sm font-medium">Day Pass: 9:00 AM - 8:00 PM</p>
                        <p className="text-xs text-muted-foreground">Fixed timing for all day passes</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-sm font-medium">Quantity (passes) *</Label>
                          <Input type="number" min={1} max={100} value={slot.pass_quantity || 1}
                            onChange={(e) => updateSlot(index, { pass_quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                            data-testid={`guest-pass-quantity-${index}`} />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm font-medium">Valid Days *</Label>
                          <Input type="number" min={1} max={365} value={slot.pass_days || 1}
                            onChange={(e) => updateSlot(index, { pass_days: Math.max(1, parseInt(e.target.value) || 1) })}
                            data-testid={`guest-pass-days-${index}`} />
                          <p className="text-[10px] text-muted-foreground">Validity window only — does not multiply price</p>
                        </div>
                      </div>
                    </div>
                  ) : mode === 'monthly' ? (
                    <div className="p-3 rounded-lg bg-primary/10 border border-primary/20" data-testid={`guest-monthly-info-${index}`}>
                      <p className="text-sm font-medium">Dedicated Desk — Monthly</p>
                      <p className="text-xs text-muted-foreground">Full month access, no time selection needed</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Start Time *</Label>
                        <Select value={slot.start_time} onValueChange={(v) => updateSlot(index, { start_time: v, end_time: '' })}>
                          <SelectTrigger data-testid={`guest-start-time-input-${index}`}><SelectValue placeholder="Start" /></SelectTrigger>
                          <SelectContent>
                            {TIME_SLOTS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">End Time *</Label>
                        <Select value={slot.end_time} onValueChange={(v) => updateSlot(index, { end_time: v })}>
                          <SelectTrigger data-testid={`guest-end-time-input-${index}`}><SelectValue placeholder="End" /></SelectTrigger>
                          <SelectContent>
                            {getEndSlots(slot.start_time).map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Discount (%){applyOffer && <span className="text-xs text-muted-foreground font-normal"> · off with the sale offer</span>}</Label>
                    <Input type="number" min={0} max={100} step="0.1" placeholder="0" value={slot.discount_percent || ''} disabled={applyOffer}
                      onChange={(e) => { const v = parseFloat(e.target.value) || 0; updateSlot(index, { discount_percent: Math.max(0, Math.min(100, v)) }); }}
                      data-testid={`guest-discount-input-${index}`} />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Parking Add-ons <span className="text-muted-foreground font-normal text-xs">(Optional)</span></Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className={`p-3 rounded-lg border ${slot.parking_qty > 0 ? 'border-primary/40 bg-primary/5' : (isDark ? 'border-[#1e2128]' : 'border-[#e5dfd0]')}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium">4-Wheeler</p>
                            <p className="text-xs text-muted-foreground">₹50/hr × spots</p>
                          </div>
                          <Input type="number" min={0} max={10} value={slot.parking_qty || 0}
                            onChange={(e) => updateSlot(index, { parking_qty: Math.max(0, parseInt(e.target.value) || 0) })}
                            className="w-16 h-8 text-sm text-center" data-testid={`guest-parking-qty-${index}`} />
                        </div>
                      </div>
                      <div className={`p-3 rounded-lg border ${slot.parking_2w_qty > 0 ? 'border-primary/40 bg-primary/5' : (isDark ? 'border-[#1e2128]' : 'border-[#e5dfd0]')}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium">2-Wheeler</p>
                            <p className="text-xs text-muted-foreground">₹20/hr × spots</p>
                          </div>
                          <Input type="number" min={0} max={20} value={slot.parking_2w_qty || 0}
                            onChange={(e) => updateSlot(index, { parking_2w_qty: Math.max(0, parseInt(e.target.value) || 0) })}
                            className="w-16 h-8 text-sm text-center" data-testid={`guest-parking-2w-qty-${index}`} />
                        </div>
                      </div>
                    </div>
                  </div>

                  {applyOffer && (
                    <OfferPreviewLine
                      preview={offerPreviewFor({
                        bookable: selectedSpace,
                        passQty: Math.max(1, parseInt(slot.pass_quantity) || 1),
                        hours: calcDurationHours(slot.start_time, slot.end_time),
                      }, promo)}
                      testId={`guest-offer-preview-${index}`}
                    />
                  )}

                  {/* Per-slot price breakup */}
                  {slot.pricePreview ? (
                    <div className={`rounded-lg border ${isDark ? 'border-[#1e2128] bg-[#0a0b0d]' : 'border-[#e5dfd0] bg-[#faf8f3]'} p-3 space-y-1.5`} data-testid={`price-breakup-${index}`}>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {slot.pricePreview.space_name}
                          {slot.pricePreview.booking_mode === 'hourly' ? ` (₹${slot.pricePreview.rate_per_hour}/hr × ${slot.pricePreview.hours}h)` : ''}
                          {slot.pricePreview.booking_mode === 'daily' ? ` (${slot.pricePreview.pass_quantity || 1} pass × ₹${slot.pricePreview.rate_per_hour})` : ''}
                        </span>
                        <span>₹{slot.pricePreview.base_cost.toLocaleString('en-IN')}</span>
                      </div>
                      {slot.pricePreview.auto_discount_amount > 0 && (
                        <div className="flex justify-between text-sm text-emerald-500">
                          <span>Auto Discount ({slot.pricePreview.auto_discount_pct}%)</span>
                          <span>- ₹{slot.pricePreview.auto_discount_amount.toLocaleString('en-IN')}</span>
                        </div>
                      )}
                      {slot.pricePreview.manual_discount > 0 && (
                        <div className="flex justify-between text-sm text-emerald-500">
                          <span>Manual Discount ({slot.pricePreview.discount_percent}%)</span>
                          <span>- ₹{slot.pricePreview.manual_discount.toLocaleString('en-IN')}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">GST ({slot.pricePreview.gst_rate}%)</span>
                        <span>₹{slot.pricePreview.gst_amount.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between text-sm font-bold border-t border-border pt-1.5 mt-1">
                        <span>Slot Total</span>
                        <span className="text-primary">₹{slot.pricePreview.total_payable.toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => handlePricePreview(index)}
                      disabled={slot.loadingPreview || !slot.bookable_id || !slot.start_time || !slot.end_time}
                      className="w-full" data-testid={`guest-preview-cost-btn-${index}`}>
                      {slot.loadingPreview ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                        <><IndianRupee className="w-4 h-4 mr-1.5" />Preview Cost</>
                      )}
                    </Button>
                  )}
                </div>
              );
            })}

            <Button type="button" variant="outline" onClick={addSlot} className="w-full border-dashed" data-testid="guest-add-slot-btn">
              <Plus className="w-4 h-4 mr-1.5" />Add New Slot
            </Button>

            {/* Grand Total — shown once every slot has a price preview */}
            {allSlotsPreviewed && (
              <div className={`rounded-xl border ${isDark ? 'border-[#1e2128] bg-[#0a0b0d]' : 'border-[#e5dfd0] bg-[#faf8f3]'} p-4`} data-testid="guest-grand-total">
                <div className="flex justify-between text-base font-bold">
                  <span>Grand Total ({slots.length} slot{slots.length !== 1 ? 's' : ''})</span>
                  <span className="text-primary">₹{grandTotal.toLocaleString('en-IN')}</span>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={guestForm.payment_method || 'razorpay'} onValueChange={(v) => setGuestForm(p => ({ ...p, payment_method: v, is_partial_payment: v === 'manual' ? false : p.is_partial_payment }))}>
                <SelectTrigger data-testid="guest-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="razorpay">Razorpay (Send Payment Link)</SelectItem>
                  <SelectItem value="manual">Manual / Cash Payment</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Partial Payment (visible once every slot has been previewed) */}
            {allSlotsPreviewed && guestForm.payment_method !== 'manual' && !applyOffer && (
              <div className="rounded-md border border-border p-3 space-y-2" data-testid="guest-partial-block">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!guestForm.is_partial_payment}
                    onChange={(e) => setGuestForm(p => ({ ...p, is_partial_payment: e.target.checked }))}
                    className="w-4 h-4 rounded border-border"
                    data-testid="guest-partial-toggle"
                  />
                  <span className="text-sm font-medium">Split into partial + balance payment</span>
                </label>
                {guestForm.is_partial_payment && (
                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <div>
                      <Label className="text-xs">Amount to collect now *</Label>
                      <div className="relative">
                        <IndianRupee className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
                        <Input
                          type="number" step="0.01" min={1}
                          max={grandTotal - 1}
                          value={guestForm.partial_amount}
                          onChange={(e) => setGuestForm(p => ({ ...p, partial_amount: e.target.value }))}
                          placeholder={`Max ₹${(grandTotal - 1).toFixed(2)}`}
                          className="pl-7" data-testid="guest-partial-amount"
                        />
                      </div>
                      {guestForm.partial_amount && parseFloat(guestForm.partial_amount) > 0 && parseFloat(guestForm.partial_amount) < grandTotal && (
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          Balance: <b>₹{(grandTotal - parseFloat(guestForm.partial_amount)).toLocaleString('en-IN')}</b>
                        </p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs">Balance due date *</Label>
                      <Input
                        type="date"
                        min={new Date().toISOString().slice(0, 10)}
                        value={guestForm.balance_due_date}
                        onChange={(e) => setGuestForm(p => ({ ...p, balance_due_date: e.target.value }))}
                        data-testid="guest-balance-due-date"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => { setShowGuestBooking(false); setSlots([emptySlot()]); }}>Cancel</Button>
            <Button onClick={handleGuestBooking}
              disabled={submitting || !allSlotsPreviewed || !guestForm.guest_name || ((guestForm.payment_method !== 'manual' || applyOffer) && !guestForm.guest_email) || slots.some(s => !s.booking_date)}
              className="bg-primary hover:bg-primary/90"
              data-testid="guest-booking-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <><Send className="w-4 h-4 mr-2" />{guestForm.payment_method === 'manual' ? 'Confirm Booking' : 'Confirm & Send Link'}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Link Success Dialog */}
      <Dialog open={!!paymentLinkResult} onOpenChange={() => setPaymentLinkResult(null)}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-[#13161a] border-[#1e2128]' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Payment Link Sent
            </DialogTitle>
            <DialogDescription>
              {paymentLinkResult?.message}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <p className="text-sm text-muted-foreground mb-1">Guest</p>
              <p className="font-medium text-sm">{paymentLinkResult?.guest_name} ({paymentLinkResult?.guest_email})</p>
            </div>
            <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
              <p className="text-sm text-muted-foreground mb-1">
                {paymentLinkResult?.is_partial_payment ? 'Total' : 'Amount'}
              </p>
              <p className="font-bold text-lg">₹{paymentLinkResult?.total_amount?.toLocaleString('en-IN')}</p>
              {paymentLinkResult?.is_partial_payment && (
                <div className="mt-2 text-xs space-y-0.5">
                  <p><span className="text-amber-700 font-medium">Partial now:</span> ₹{paymentLinkResult?.partial_amount?.toLocaleString('en-IN')}</p>
                  <p><span className="text-muted-foreground">Balance (due {paymentLinkResult?.balance_due_date}):</span> ₹{paymentLinkResult?.balance_amount?.toLocaleString('en-IN')}</p>
                </div>
              )}
            </div>
            <OfferRewardsNotice saleOffer={paymentLinkResult?.sale_offer} />
            {paymentLinkResult?.slots?.length > 1 && (
              <div className="space-y-1.5" data-testid="payment-link-slots">
                <p className="text-sm font-medium">{paymentLinkResult.slots.length} Slots Booked</p>
                {paymentLinkResult.slots.map((s) => (
                  <div key={s.booking_id} className="flex items-center justify-between text-xs p-2 rounded-lg bg-muted/50">
                    <span>{s.bookable_name} · {s.venue_name} · {s.booking_date}, {s.start_time}-{s.end_time}</span>
                    <span className="font-medium whitespace-nowrap ml-2">₹{s.total_amount?.toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            )}
            {paymentLinkResult?.payment_link && (
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  {paymentLinkResult?.is_partial_payment ? `Partial Payment Link (₹${paymentLinkResult?.partial_amount?.toLocaleString('en-IN')})` : 'Payment Link'}
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={paymentLinkResult.payment_link}
                    className="text-xs"
                    data-testid="payment-link-url"
                  />
                  <Button size="sm" variant="outline" onClick={copyPaymentLink} data-testid="copy-payment-link-btn">
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-green-600 hover:text-green-700 hover:bg-green-500/10"
                    onClick={() => shareViaWhatsApp(
                      paymentLinkResult?.is_partial_payment
                        ? `Hi ${paymentLinkResult?.guest_name || ''}, here's your Clayworks partial payment link for ₹${paymentLinkResult?.partial_amount?.toLocaleString('en-IN')} (balance ₹${paymentLinkResult?.balance_amount?.toLocaleString('en-IN')} due by ${paymentLinkResult?.balance_due_date}): ${paymentLinkResult.payment_link}`
                        : `Hi ${paymentLinkResult?.guest_name || ''}, here's your Clayworks payment link for ₹${paymentLinkResult?.total_amount?.toLocaleString('en-IN')}: ${paymentLinkResult.payment_link}`,
                      paymentLinkResult?.guest_phone
                    )}
                    data-testid="whatsapp-payment-link-btn"
                    title="Share via WhatsApp"
                  >
                    <WhatsAppIcon className="w-4 h-4" />
                  </Button>
                  <a href={paymentLinkResult.payment_link} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="outline" data-testid="open-payment-link-btn">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </a>
                </div>
                {paymentLinkResult?.is_partial_payment && paymentLinkResult?.balance_payment_link && (
                  <>
                    <p className="text-sm font-medium pt-2">
                      Balance Payment Link (₹{paymentLinkResult?.balance_amount?.toLocaleString('en-IN')} · due {paymentLinkResult?.balance_due_date})
                    </p>
                    <div className="flex items-center gap-2">
                      <Input
                        readOnly
                        value={paymentLinkResult.balance_payment_link}
                        className="text-xs"
                        data-testid="balance-payment-link-url"
                      />
                      <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(paymentLinkResult.balance_payment_link); toast.success('Balance link copied'); }} data-testid="copy-balance-link-btn">
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-green-600 hover:text-green-700 hover:bg-green-500/10"
                        onClick={() => shareViaWhatsApp(
                          `Hi ${paymentLinkResult?.guest_name || ''}, here's the balance payment link for ₹${paymentLinkResult?.balance_amount?.toLocaleString('en-IN')} (due by ${paymentLinkResult?.balance_due_date}): ${paymentLinkResult.balance_payment_link}`,
                          paymentLinkResult?.guest_phone
                        )}
                        title="Share balance via WhatsApp"
                      >
                        <WhatsAppIcon className="w-4 h-4" />
                      </Button>
                      <a href={paymentLinkResult.balance_payment_link} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </a>
                    </div>
                  </>
                )}
                <p className="text-xs text-muted-foreground">Share this link with the guest via WhatsApp or other channels</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setPaymentLinkResult(null)} data-testid="payment-link-done-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ProfileModal
        open={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        user={user}
        api={api}
        onProfileUpdate={() => window.location.reload()}
      />
      <SellCreditsModal open={showSellCredits} onClose={setShowSellCredits} onLinkSent={setPaymentLinkResult} />
      <MiscPaymentLinkModal
        open={showMiscPay}
        onClose={() => setShowMiscPay(false)}
        api={api}
        venues={venues}
        onCreated={() => { fetchData(); }}
      />
      <CreateClientModal
        open={showCreateClient}
        onClose={() => setShowCreateClient(false)}
        api={api}
      />
      <RescheduleBookingModal
        open={!!rescheduleBooking}
        onClose={() => setRescheduleBooking(null)}
        booking={rescheduleBooking}
        api={api}
        venues={venues}
        onDone={fetchData}
      />
    </div>
  );
};

const SpacesShareView = ({ venues, bookables, userId, isDark }) => {
  const [search, setSearch] = useState('');
  const [venueFilter, setVenueFilter] = useState('all');
  const [copiedId, setCopiedId] = useState(null);

  const baseUrl = window.location.origin;

  const getShareLink = (bookableId) => {
    return `${baseUrl}/preview/space/${bookableId}${userId ? `?ref=${userId}` : ''}`;
  };

  const copyLink = (bookableId) => {
    navigator.clipboard.writeText(getShareLink(bookableId));
    setCopiedId(bookableId);
    toast.success('Preview link copied to clipboard!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filtered = bookables
    .filter(b => b.is_active !== false)
    .filter(b => venueFilter === 'all' || b.venue_id === venueFilter)
    .filter(b => {
      if (!search) return true;
      const q = search.toLowerCase();
      const vName = venues.find(v => v.id === b.venue_id)?.name || '';
      return b.name?.toLowerCase().includes(q) || vName.toLowerCase().includes(q) || b.bookable_type?.toLowerCase().includes(q);
    });

  const cardBg = isDark ? 'bg-card' : 'bg-white';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold">Share Spaces with Clients</h2>
        <p className="text-sm text-muted-foreground mt-1">Generate preview links to share meeting rooms and day passes. Clients can view details and book without logging in first.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          placeholder="Search spaces..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="sm:w-64"
          data-testid="spaces-search-input"
        />
        <Select value={venueFilter} onValueChange={setVenueFilter}>
          <SelectTrigger className="sm:w-52" data-testid="spaces-venue-filter">
            <SelectValue placeholder="All Venues" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Venues</SelectItem>
            {venues.filter(v => v.is_active).map(v => (
              <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(b => {
          const venue = venues.find(v => v.id === b.venue_id);
          const mode = b.booking_mode || 'hourly';
          const rateLabel = mode === 'daily' ? '/day' : mode === 'monthly' ? '/month' : '/hr';
          const typeLabel = {
            meeting_room: 'Meeting Room', conference_room: 'Conference Room',
            day_pass: 'Day Pass', dedicated_desk: 'Dedicated Desk',
            event_space: 'Event Space', virtual_office: 'Virtual Office',
          }[b.bookable_type] || b.bookable_type;

          return (
            <Card key={b.id} className={`${cardBg} overflow-hidden hover:shadow-md transition-shadow`}>
              {(b.images?.[0] || venue?.images?.[0]) && (
                <div className="h-36 overflow-hidden">
                  <img src={b.images?.[0] || venue?.images?.[0]} alt={b.name} className="w-full h-full object-cover" />
                </div>
              )}
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-semibold text-sm">{b.name}</h3>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Building2 className="w-3 h-3" />{venue?.name || 'Unknown'}
                    </p>
                  </div>
                  <Badge variant="secondary" className="text-xs shrink-0">{typeLabel}</Badge>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><IndianRupee className="w-3 h-3" />{b.hourly_rate?.toLocaleString('en-IN')}{rateLabel}</span>
                  <span className="flex items-center gap-1"><UserPlus className="w-3 h-3" />{b.capacity} seats</span>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 text-xs"
                    onClick={() => copyLink(b.id)}
                    data-testid={`share-copy-${b.id}`}
                  >
                    {copiedId === b.id ? <CheckCircle2 className="w-3.5 h-3.5 mr-1 text-green-600" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                    {copiedId === b.id ? 'Copied!' : 'Copy Link'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs text-green-600 hover:text-green-700 hover:bg-green-500/10"
                    onClick={() => shareViaWhatsApp(`Check out ${b.name} at ${venue?.name || 'Clayworks'}: ${getShareLink(b.id)}`)}
                    data-testid={`share-whatsapp-${b.id}`}
                    title="Share via WhatsApp"
                  >
                    <WhatsAppIcon className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="default"
                    className="text-xs"
                    onClick={() => window.open(getShareLink(b.id), '_blank')}
                    data-testid={`share-preview-${b.id}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1" />
                    Preview
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      {filtered.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <Share2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No spaces found matching your search.</p>
        </div>
      )}
    </div>
  );
};

export default SalesDashboard;
